import React from 'react';
import moment, { Moment } from 'moment';
import { Col, Row } from 'antd';
import { getLengthInWeeks } from 'works/legacy/services/length-in-weeks-service';
import { Divider, Gap, StatePill, Text } from '@common/components';
import { toUSFormat } from 'works/legacy/lib/date-helper';
import { OfferChangeRequestType } from '@applicant-flow/graphql/';

export function OfferInfoDetails({
  offerChangeRequests,
  startDate,
  endDate,
  timeoffDescription,
  shiftType,
  shiftsPerWeek,
  hoursPerShift,
  overtimeThreshold,
}: {
  offerChangeRequests: OfferChangeRequestType[];
  startDate: string;
  endDate: string;
  shiftType: string;
  shiftsPerWeek: number;
  hoursPerShift: number;
  timeoffDescription?: string | null;
  overtimeThreshold?: number | null;
}) {
  const _startDate = moment(startDate);
  const _endDate = moment(endDate);
  const lengthText = getLengthInWeeks(_startDate, _endDate);

  const offerChangeRequest =
    offerChangeRequests && offerChangeRequests.length > 0
      ? offerChangeRequests[offerChangeRequests.length - 1]
      : null;

  const detail =
    offerChangeRequest?.offerChangeResponse?.detail ||
    ({} as {
      oldStartDate?: string;
      oldEndDate?: string;
      oldShiftType?: string;
      oldShiftsPerWeek?: number;
      oldHoursPerShift?: number;
      oldTimeoffDescription?: string;
    });

  const {
    oldStartDate,
    oldEndDate,
    oldShiftType,
    oldShiftsPerWeek,
    oldHoursPerShift,
    oldTimeoffDescription,
  } = detail;

  const oldFormattedStartDate = oldStartDate ? toUSFormat(oldStartDate) : null;

  const oldFormattedEndDate = oldEndDate ? toUSFormat(oldEndDate) : null;

  const oldLengthText = getOldLengthInWeeks(
    _startDate,
    _endDate,
    oldStartDate ? moment(oldStartDate) : null,
    oldEndDate ? moment(oldEndDate) : null
  );

  return (
    <>
      <Row>
        <Col span={8}>
          <DetailItem
            label="Start date"
            value={toUSFormat(_startDate)}
            prevValue={oldFormattedStartDate}
          />
        </Col>
        <Col span={8}>
          <DetailItem
            label="End date"
            value={toUSFormat(_endDate)}
            prevValue={oldFormattedEndDate}
          />
        </Col>
        <Col span={8}>
          <DetailItem
            label="Length"
            value={lengthText}
            prevValue={oldLengthText}
          />
        </Col>
      </Row>
      <Divider gutter={16} />
      <DetailItem
        label="Time off"
        value={timeoffDescription || ''}
        prevValue={oldTimeoffDescription}
      />
      <Divider gutter={16} />
      <Row>
        <Col span={8}>
          <DetailItem
            label="Shift type"
            value={shiftType || ''}
            prevValue={oldShiftType}
          />
        </Col>
        <Col span={8}>
          <DetailItem
            label="Shifts per week"
            value={shiftsPerWeek || 0}
            prevValue={oldShiftsPerWeek}
          />
        </Col>
        <Col span={8}>
          <DetailItem
            label="Hours per shift"
            value={hoursPerShift || 0}
            prevValue={oldHoursPerShift}
          />
        </Col>
      </Row>
      {!!overtimeThreshold && (
        <>
          <Divider gutter={16} />
          <DetailItem
            label="Overtime Threshold"
            value={`${overtimeThreshold} hours`}
          />
        </>
      )}
    </>
  );
}

const getOldLengthInWeeks = (
  startDate: Moment,
  endDate: Moment,
  oldStartDate: Moment | null,
  oldEndDate: Moment | null
) => {
  if (oldStartDate && oldEndDate) {
    return getLengthInWeeks(oldStartDate, oldEndDate);
  }

  if (oldStartDate) {
    return getLengthInWeeks(oldStartDate, endDate);
  }

  if (oldEndDate) {
    return getLengthInWeeks(startDate, oldEndDate);
  }

  return null;
};

const DetailItem = ({
  label,
  value,
  prevValue,
}: {
  label: string;
  value: string | number;
  prevValue?: string | number | null;
}) => (
  <Gap vertical size="xs">
    <div>{label}</div>
    {prevValue && prevValue !== value ? (
      <Gap align="center">
        <Text deleted color="darkGrey">
          {prevValue}
        </Text>
        <StatePill color="grass">{value}</StatePill>
      </Gap>
    ) : (
      <Text weight="bold">{value}</Text>
    )}
  </Gap>
);
